import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Button,
  Menu,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import AddIcon from "@mui/icons-material/Add";
import EnrolledProgram from "./EnrolledProgram";
import dayjs from "dayjs";
import {
  fetchAssessmentReferenceData,
  getCachedAssessmentReferenceData,
  checkIsFormless,
  cleanProgramId,
  normalizeProgramId,
  isEnrolledOutcome,
  isProgramCompletionOutcome,
  hasTerminalOutcome,
} from "../../api/assessment";

// Helper to look up a program outcome from selectedOutcome map
const getOutcomeForProgram = (prog, selectedOutcomeMap) => {
  if (!selectedOutcomeMap || !prog) return "";
  const canonicalId = String(prog.id || "")
    .toUpperCase()
    .trim();

  if (selectedOutcomeMap[prog.id]) return selectedOutcomeMap[prog.id];
  if (selectedOutcomeMap[canonicalId]) return selectedOutcomeMap[canonicalId];
  if (prog.programId && selectedOutcomeMap[prog.programId]) {
    return selectedOutcomeMap[prog.programId];
  }
  if (prog.name && selectedOutcomeMap[prog.name]) {
    return selectedOutcomeMap[prog.name];
  }
  return "";
};

const pickDisplayName = (source) =>
  source.fullName ||
  `${source.firstName || ""} ${source.lastName || ""}`.trim() ||
  source.displayName ||
  source.name ||
  source.username ||
  "";

const getNameFromStoredUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails") || "{}");
    return pickDisplayName(user);
  } catch (e) {
    // ignore
  }
  return "";
};

const getNameFromToken = () => {
  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      "";
    if (token?.includes(".")) {
      const parts = token.split(".");
      if (parts.length >= 2) {
        const payload = JSON.parse(
          atob(parts[1].replaceAll("-", "+").replaceAll("_", "/")),
        );
        return pickDisplayName(payload);
      }
    }
  } catch (e) {
    // ignore
  }
  return "";
};

const getLoggedInUserName = () => getNameFromStoredUser() || getNameFromToken();

const matchesOutcome = (o, outcomeVal, norm) =>
  o.outcomeName === outcomeVal ||
  o.displayName === outcomeVal ||
  o.outcome === outcomeVal ||
  String(o.outcomeName || "")
    .toUpperCase()
    .trim() === norm ||
  String(o.displayName || "")
    .toUpperCase()
    .trim() === norm ||
  String(o.outcome || "")
    .toUpperCase()
    .trim() === norm;

const buildSubReasonMap = (outcomes) => {
  const reasons = {};
  outcomes.forEach((o) => {
    if (o.subReasons && o.subReasons.length > 0) {
      const list = o.subReasons.map(
        (s) => s.displayName || s.subReason || s.reason || s.name || s,
      );
      if (o.displayName) reasons[o.displayName] = list;
      if (o.outcomeName && !reasons[o.outcomeName])
        reasons[o.outcomeName] = list;
      if (o.outcome && !reasons[o.outcome]) reasons[o.outcome] = list;
    }
  });
  return reasons;
};

const isExcludedProgram = (canonicalId, progName) =>
  canonicalId.includes("BIOSIMILAR") ||
  progName.toUpperCase().includes("BIOSIMILAR") ||
  canonicalId.includes("COPAY") ||
  canonicalId.includes("SCRIPTCO") ||
  canonicalId.includes("METADATA") ||
  canonicalId.includes("LIFESTYLE") ||
  canonicalId.includes("CARE_PLAN") ||
  canonicalId.includes("CAREPLAN") ||
  canonicalId === "GENERAL" ||
  canonicalId === "OTHER";

const findMatchedActiveProgram = (activePrograms, progId, progName) => {
  const canonicalId = progId.toUpperCase();
  return (activePrograms || []).find((ap) => {
    const apId = String(ap.id || ap.programId || "")
      .toUpperCase()
      .trim();
    const apName = String(
      ap.name || ap.displayName || ap.programDisplayName || "",
    )
      .toUpperCase()
      .trim();
    const apClean = cleanProgramId(apId);
    const pClean = cleanProgramId(progId);
    return (
      apId === canonicalId ||
      apClean === canonicalId ||
      apId === progId.toUpperCase() ||
      (pClean && apClean === pClean) ||
      normalizeProgramId(apName) === normalizeProgramId(progName) ||
      apName === progName.toUpperCase()
    );
  });
};

const resolveDefaultModalType = (
  matchedActive,
  initialModalAssessmentTypes,
  progId,
  progName,
  assessmentType,
) =>
  matchedActive?.defaultAssessmentType ||
  matchedActive?.initialFormType ||
  matchedActive?.formType ||
  matchedActive?.assessmentType ||
  initialModalAssessmentTypes?.[progId.toUpperCase()] ||
  initialModalAssessmentTypes?.[cleanProgramId(progId).toUpperCase()] ||
  initialModalAssessmentTypes?.[cleanProgramId(progName).toUpperCase()] ||
  initialModalAssessmentTypes?.[normalizeProgramId(progId)] ||
  initialModalAssessmentTypes?.[normalizeProgramId(progName)] ||
  (assessmentType || "INITIAL").toUpperCase();

const resolveFormType = ({
  isFormless,
  isProgCompletion,
  isProgEnrolled,
  matchedActive,
  defaultModalType,
}) => {
  if (isFormless) return "N/A";
  if (isProgCompletion) return "DISCHARGE";
  const activeType = matchedActive?.formType || matchedActive?.assessmentType;
  if (isProgEnrolled) return activeType || defaultModalType;
  return activeType || defaultModalType || "INITIAL";
};

const getHoverBorderColor = (assessmentType, isAssessmentTypeDisabled) => {
  if (!assessmentType) return "#D32F2F !important";
  return isAssessmentTypeDisabled ? "#E8E8E9" : "#403C6F";
};

const getFormattedTime = (isoString) => {
  if (!isoString) return "";
  return dayjs(isoString).format("MM/DD/YYYY hh:mm A");
};

const getElementWidthPx = (element) =>
  element ? `${element.offsetWidth}px` : undefined;

const getRequiredErrorColor = (hasError) =>
  hasError ? "#D32F2F !important" : undefined;

const getReadOnlyFieldStyles = (readOnly, borderColor, primaryColor) => ({
  fieldBackground: readOnly ? "#F3F4F6" : "#fff",
  hoverBorderColor: readOnly ? borderColor : primaryColor,
  inputCursor: readOnly ? "not-allowed" : "default",
});

const resolveInitialTime = (readOnly, noteCompletionTime, noteStartTime) => {
  const nowFormatted = dayjs().format("MM/DD/YYYY hh:mm A");
  if (readOnly) {
    return (
      getFormattedTime(noteCompletionTime) ||
      getFormattedTime(noteStartTime) ||
      nowFormatted
    );
  }
  return getFormattedTime(noteStartTime) || nowFormatted;
};

const resolveSigneeToUse = (readOnly, resolvedAssigneeRn) =>
  readOnly
    ? resolvedAssigneeRn || getLoggedInUserName()
    : getLoggedInUserName() || resolvedAssigneeRn;

const OutcomeAndSign = ({
  memberData = {},
  assigneeRnName = "",
  signeeName = "",
  secondaryColor,
  borderColor,
  primaryColor,
  selectedOutcome = {},
  setSelectedOutcome,
  clinicalNotes = "",
  setClinicalNotes,
  certification = false,
  setCertification,
  outcomes = [],
  readOnly = false,
  noteStartTime = null,
  noteCompletionTime = null,
  declinedReason = {},
  setDeclinedReason,
  selectedSubReason = {},
  setSelectedSubReason,
  declinedNotes = {},
  setDeclinedNotes,
  programMap,
  activePrograms = [],
  initialModalAssessmentTypes = {},
  referenceData: propReferenceData = null,
  showOutcomesOnly = false,
  showSignOnly = false,
  showPreAssessmentHeader = false,
  showNotesAndSign = false,
  availableProgramGroups = [],
  isAddProgramDisabled = false,
  showAssessmentTypeSelect = false,
  isAssessmentTypeDisabled = false,
  assessmentType = "",
  onAssessmentTypeChange = null,
  onProgramAssessmentTypeChange = null,
  onAddProgram = null,
  onRemoveProgram = null,
}) => {
  const [addProgramAnchorEl, setAddProgramAnchorEl] = useState(null);

  const initialTime = resolveInitialTime(
    readOnly,
    noteCompletionTime,
    noteStartTime,
  );
  const [currentTime, setCurrentTime] = useState(initialTime);
  const [referenceData, setReferenceData] = useState(
    () => propReferenceData || getCachedAssessmentReferenceData() || null,
  );

  const resolvedAssigneeRn =
    assigneeRnName ||
    memberData?.assigneeRnName ||
    memberData?.assignee_rn_name ||
    memberData?.member?.assigneeRnName ||
    memberData?.assigneeName ||
    memberData?.assignee?.name ||
    signeeName ||
    memberData?.submittedBy ||
    memberData?.submittedByName ||
    "";

  const getFormattedSigneeName = (rawName) => {
    const trimmed = String(rawName || "").trim();
    if (!trimmed || trimmed === "N/A" || trimmed === "—") {
      return "";
    }
    if (/\b(RN|NP|MD|DO|PA|LPN)\b/i.test(trimmed)) {
      return trimmed;
    }
    return `${trimmed}, RN`;
  };

  const rawSigneeToUse = resolveSigneeToUse(readOnly, resolvedAssigneeRn);

  const displaySigneeName = getFormattedSigneeName(rawSigneeToUse);

  useEffect(() => {
    if (readOnly) {
      setCurrentTime(
        getFormattedTime(noteCompletionTime) ||
          getFormattedTime(noteStartTime) ||
          "",
      );
      return;
    }
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format("MM/DD/YYYY hh:mm A"));
    }, 1000);
    return () => clearInterval(timer);
  }, [readOnly, noteCompletionTime, noteStartTime]);

  useEffect(() => {
    if (propReferenceData) {
      setReferenceData(propReferenceData);
      return;
    }
    const cached = getCachedAssessmentReferenceData();
    if (cached) {
      setReferenceData(cached);
    }
    const getRefData = async () => {
      try {
        const data = await fetchAssessmentReferenceData();
        if (data) {
          setReferenceData(data);
        }
      } catch (error) {
        console.error("Failed to fetch assessment reference data:", error);
      }
    };
    void getRefData();
  }, [propReferenceData]);

  const getOutcomeDisplayName = (outcomeVal) => {
    if (!outcomeVal) return "";
    const norm = String(outcomeVal).toUpperCase().trim();
    const matched = referenceData?.outcomes?.find((o) =>
      matchesOutcome(o, outcomeVal, norm),
    );
    if (matched?.displayName) return matched.displayName;
    return outcomeVal;
  };

  const filteredReferenceOutcomes = React.useMemo(() => {
    return referenceData?.outcomes || [];
  }, [referenceData]);

  const outcomesList = React.useMemo(() => {
    if (filteredReferenceOutcomes.length > 0) {
      const names = [];
      filteredReferenceOutcomes.forEach((o) => {
        const name = o.displayName || o.name || o.outcomeName || o.outcome;
        if (name && !names.includes(name)) {
          names.push(name);
        }
      });
      if (names.length > 0) {
        return names;
      }
    }
    if (outcomes && outcomes.length > 0) {
      return outcomes;
    }
    return [];
  }, [filteredReferenceOutcomes, outcomes]);

  const dynamicOutcomeReasons = React.useMemo(
    () => buildSubReasonMap(filteredReferenceOutcomes),
    [filteredReferenceOutcomes],
  );

  const formlessOutcomeReasons = React.useMemo(
    () => buildSubReasonMap(referenceData?.outcomes || []),
    [referenceData],
  );

  // Cleanly map incoming programMap to list of enrolled programs
  const programsList = React.useMemo(() => {
    const list = [];
    const seen = new Set();
    Object.entries(programMap || {}).forEach(([id, name]) => {
      if (!id && !name) return;
      const progId = String(id).trim();
      const progName = String(name || id).trim();
      const canonicalId = progId.toUpperCase();

      // Exclude non-clinical or global forms
      if (isExcludedProgram(canonicalId, progName)) {
        return;
      }

      if (seen.has(canonicalId)) return;
      seen.add(canonicalId);

      const isFormless = checkIsFormless(
        { id: progId, name: progName },
        referenceData,
      );

      const matchedActive = findMatchedActiveProgram(
        activePrograms,
        progId,
        progName,
      );

      const progOutcome = getOutcomeForProgram(
        { id: progId, name: progName },
        selectedOutcome,
      );
      const isProgEnrolled = isEnrolledOutcome(progOutcome, referenceData);
      const isProgCompletion = isProgramCompletionOutcome(
        progOutcome,
        referenceData,
      );

      const defaultModalType = resolveDefaultModalType(
        matchedActive,
        initialModalAssessmentTypes,
        progId,
        progName,
        assessmentType,
      );

      const resolvedFormType = resolveFormType({
        isFormless,
        isProgCompletion,
        isProgEnrolled,
        matchedActive,
        defaultModalType,
      });

      const progObj = {
        id: progId,
        programId: progId,
        name: progName,
        isFormless,
        manuallyAdded: Boolean(matchedActive?.manuallyAdded),
        initialFormType: defaultModalType,
        defaultAssessmentType: defaultModalType,
        formType: resolvedFormType,
        assessmentType: resolvedFormType,
      };

      if (readOnly) {
        const checkOutcome = getOutcomeForProgram(progObj, selectedOutcome);
        if (!checkOutcome) return;
      }

      list.push(progObj);
    });
    return list;
  }, [
    programMap,
    readOnly,
    selectedOutcome,
    referenceData,
    activePrograms,
    assessmentType,
    initialModalAssessmentTypes,
  ]);

  // Available programs that can be added
  const addableProgramsList = React.useMemo(() => {
    const rawPrograms =
      referenceData?.programs && Array.isArray(referenceData.programs)
        ? referenceData.programs
        : availableProgramGroups || [];

    if (!Array.isArray(rawPrograms) || rawPrograms.length === 0) {
      return [];
    }

    const currentKeys = new Set();
    programsList.forEach((p) => {
      if (p.id) currentKeys.add(String(p.id).toUpperCase().trim());
      if (p.programId)
        currentKeys.add(String(p.programId).toUpperCase().trim());
      if (p.name) currentKeys.add(String(p.name).toUpperCase().trim());
    });

    const result = [];
    const seen = new Set();

    rawPrograms.forEach((p) => {
      if (!p) return;
      const rawName = String(
        p.name || p.displayName || p.groupName || p.programName || "",
      ).trim();
      const rawCode = String(
        p.programCode || p.programId || p.id || p.program || rawName,
      ).trim();

      const upperName = rawName.toUpperCase();
      const upperCode = rawCode.toUpperCase();
      const gType = String(p.groupType || "").toUpperCase();

      if (checkIsFormless(p, referenceData)) return;
      if (hasTerminalOutcome(p, referenceData)) return;

      if (
        upperName.includes("BIOSIMILAR") ||
        upperCode.includes("BIOSIMILAR") ||
        gType.includes("BIOSIMILAR") ||
        upperName === "OTHERS" ||
        upperName === "OTHER" ||
        upperCode === "OTHERS" ||
        upperCode === "OTHER" ||
        gType === "OTHER" ||
        upperCode === "GENERAL" ||
        upperCode === "LIFESTYLE" ||
        upperCode === "CARE_PLAN" ||
        upperCode === "METADATA" ||
        gType === "GENERAL" ||
        gType === "LIFESTYLE" ||
        gType === "CARE_PLAN"
      ) {
        return;
      }

      if (currentKeys.has(upperCode) || currentKeys.has(upperName)) {
        return;
      }

      const dedupeKey = upperCode || upperName;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      result.push({
        ...p,
        id: rawCode || rawName,
        programId: rawCode || rawName,
        name: rawName || rawCode,
        groupName: rawName || rawCode,
        displayName: p.displayName || rawName || rawCode,
        programDisplayName: p.displayName || rawName || rawCode,
        programCode: rawCode,
      });
    });

    return result;
  }, [referenceData, availableProgramGroups, programsList]);

  const allProgramsDeclinedOrUnableToReach = React.useMemo(() => {
    if (!programsList || programsList.length === 0) return false;
    return programsList.every((prog) => {
      const outcome = getOutcomeForProgram(prog, selectedOutcome);
      if (!outcome) return false;
      const norm = String(outcome).toUpperCase().trim();
      return (
        !isProgramCompletionOutcome(outcome, referenceData) &&
        !norm.includes("COMPLET") &&
        (norm.includes("DECLIN") ||
          norm.includes("REFUS") ||
          norm.includes("UNABLE") ||
          norm.includes("UNREACH") ||
          norm.includes("NOT_REACHED") ||
          norm.includes("DO NOT CALL") ||
          norm.includes("DO_NOT_CALL") ||
          norm.includes("NOT ENROLL") ||
          norm.includes("NOT_ENROLL"))
      );
    });
  }, [programsList, selectedOutcome, referenceData]);

  const isOnlyFormlessAvailable = React.useMemo(() => {
    if (!programsList || programsList.length === 0) return false;
    return programsList.every(
      (prog) => prog.isFormless || checkIsFormless(prog, referenceData),
    );
  }, [programsList, referenceData]);

  const hasOtherEnrolledProgram = React.useMemo(() => {
    if (!programsList || programsList.length === 0) return false;
    return programsList.some((prog) => {
      if (prog.isFormless || checkIsFormless(prog, referenceData)) return false;
      const outcome = getOutcomeForProgram(prog, selectedOutcome);
      if (!outcome) return false;
      const norm = String(outcome).toUpperCase().trim();
      return (
        isEnrolledOutcome(outcome, referenceData) ||
        isProgramCompletionOutcome(outcome, referenceData) ||
        (!norm.includes("NOT") &&
          !norm.includes("NON") &&
          (norm.startsWith("ENROLL") ||
            norm.includes("PROCEED") ||
            norm.includes("ACCEPT") ||
            norm.includes("AGREE") ||
            norm === "YES" ||
            norm.includes("COMPLET")))
      );
    });
  }, [programsList, selectedOutcome, referenceData]);

  const shouldShowPreAssessmentHeader =
    showOutcomesOnly || showPreAssessmentHeader;

  const shouldShowNotesAndSign =
    !showOutcomesOnly ||
    showNotesAndSign ||
    allProgramsDeclinedOrUnableToReach ||
    isOnlyFormlessAvailable ||
    !hasOtherEnrolledProgram;

  const headerAssessmentRef = React.useRef(null);

  const headerAssessmentWidth = getElementWidthPx(headerAssessmentRef.current);
  const assessmentTypeErrorColor = getRequiredErrorColor(!assessmentType);
  const { fieldBackground, hoverBorderColor, inputCursor } =
    getReadOnlyFieldStyles(readOnly, borderColor, primaryColor);
  const certificationErrorColor = getRequiredErrorColor(
    !readOnly && !certification,
  );

  return (
    <Box
      sx={{
        border: "none",
        padding: 0,
        margin: 0,
        minWidth: 0,
      }}
    >
      {shouldShowPreAssessmentHeader && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 },
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                color: "#403C6F",
                fontSize: "15px",
                letterSpacing: "0.5px",
                mb: 0.5,
              }}
            >
              Outreach Program Outcomes
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              {showAssessmentTypeSelect && (
                <FormControl
                  ref={headerAssessmentRef}
                  size="small"
                  error={!assessmentType}
                  sx={{ minWidth: 190 }}
                >
                  <Select
                    displayEmpty
                    error={!assessmentType}
                    value={assessmentType || ""}
                    disabled={Boolean(readOnly || isAssessmentTypeDisabled)}
                    onChange={(e) => onAssessmentTypeChange?.(e.target.value)}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: {
                          backgroundColor: "#6E6D92",
                          color: "#fff",
                          borderRadius: "10px",
                          boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                          mt: 0.5,
                          boxSizing: "border-box",
                          width: headerAssessmentWidth ?? "auto",
                          minWidth: headerAssessmentWidth,
                          maxWidth: headerAssessmentWidth ?? "100%",
                          "& .MuiMenuItem-root": {
                            fontSize: "13px",
                            color: "#fff",
                            py: 1,
                            px: 2,
                            borderRadius: "4px",
                            mx: 0.5,
                            my: 0.25,
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.15)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#403C6F !important",
                              fontWeight: 600,
                              "&:hover": {
                                backgroundColor: "#403C6F !important",
                              },
                            },
                          },
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span
                            style={{
                              color: "#D32F2F",
                              fontSize: "13px",
                              fontWeight: 500,
                            }}
                          >
                            Select Assessment Type *
                          </span>
                        );
                      }
                      const labels = {
                        INITIAL: "Initial Assessment",
                        FOLLOW_UP: "Follow-up Assessment",
                        DISCHARGE: "Discharge Assessment",
                      };
                      return (
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#403C6F",
                            fontWeight: 500,
                          }}
                        >
                          {labels[selected] || selected}
                        </span>
                      );
                    }}
                    sx={{
                      height: "32px",
                      fontSize: "13px",
                      borderRadius: "6px",
                      backgroundColor: isAssessmentTypeDisabled
                        ? "#F3F4F6"
                        : "#F7F8FB",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: assessmentTypeErrorColor ?? "#E8E8E9",
                        borderWidth: "1px !important",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: getHoverBorderColor(
                          assessmentType,
                          isAssessmentTypeDisabled,
                        ),
                        borderWidth: "1px !important",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: assessmentTypeErrorColor ?? "#403C6F",
                        borderWidth: "1px !important",
                      },
                      "&.Mui-focused:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: assessmentTypeErrorColor ?? "#403C6F",
                        borderWidth: "1px !important",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#F3F4F6",
                        cursor: "not-allowed",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E8E8E9 !important",
                          borderWidth: "1px !important",
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value=""
                      disabled
                      sx={{ fontSize: "13px", color: "#9F9DB6" }}
                    >
                      <em>Select Assessment Type</em>
                    </MenuItem>
                    <MenuItem value="INITIAL" sx={{ fontSize: "13px" }}>
                      Initial Assessment
                    </MenuItem>
                    <MenuItem value="FOLLOW_UP" sx={{ fontSize: "13px" }}>
                      Follow-up Assessment
                    </MenuItem>
                    <MenuItem value="DISCHARGE" sx={{ fontSize: "13px" }}>
                      Discharge Assessment
                    </MenuItem>
                  </Select>
                </FormControl>
              )}

              {onAddProgram && !readOnly && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={isAddProgramDisabled}
                    onClick={(e) => setAddProgramAnchorEl(e.currentTarget)}
                    startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
                    sx={{
                      borderRadius: "6px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "13px",
                      color: isAddProgramDisabled ? "#9F9DB6" : "#403C6F",
                      borderColor: "#E8E8E9",
                      backgroundColor: isAddProgramDisabled
                        ? "#F3F4F6"
                        : "#F7F8FB",
                      px: 2,
                      py: 0.5,
                      "&:hover": {
                        backgroundColor: isAddProgramDisabled
                          ? "#F3F4F6"
                          : "rgba(64, 60, 111, 0.08)",
                        borderColor: isAddProgramDisabled
                          ? "#E8E8E9"
                          : "#403C6F",
                      },
                      "&.Mui-disabled": {
                        color: "#9F9DB6",
                        backgroundColor: "#F3F4F6",
                        borderColor: "#E8E8E9",
                        cursor: "not-allowed",
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    Add Program
                  </Button>
                  <Menu
                    anchorEl={addProgramAnchorEl}
                    open={Boolean(addProgramAnchorEl)}
                    onClose={() => setAddProgramAnchorEl(null)}
                  >
                    {addableProgramsList.length > 0 ? (
                      addableProgramsList.map((g) => (
                        <MenuItem
                          key={g.programId || g.id || g.groupName}
                          onClick={() => {
                            onAddProgram(g);
                            setAddProgramAnchorEl(null);
                          }}
                        >
                          {g.programDisplayName ||
                            g.groupName ||
                            g.name ||
                            g.displayName ||
                            g.programId}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        No additional programs available
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Dynamic Outcomes for each CG+ program */}
      {!showSignOnly &&
        programsList.map((prog) => (
          <EnrolledProgram
            key={prog.id}
            prog={prog}
            readOnly={readOnly}
            primaryColor={primaryColor}
            borderColor={borderColor}
            onRemoveProgram={onRemoveProgram}
            onAssessmentTypeChange={
              onProgramAssessmentTypeChange || onAssessmentTypeChange
            }
            selectedOutcome={selectedOutcome}
            setSelectedOutcome={setSelectedOutcome}
            selectedSubReason={selectedSubReason}
            setSelectedSubReason={setSelectedSubReason}
            declinedReason={declinedReason}
            setDeclinedReason={setDeclinedReason}
            declinedNotes={declinedNotes}
            setDeclinedNotes={setDeclinedNotes}
            referenceData={referenceData}
            outcomesList={outcomesList}
            formlessOutcomeReasons={formlessOutcomeReasons}
            dynamicOutcomeReasons={dynamicOutcomeReasons}
            getOutcomeDisplayName={getOutcomeDisplayName}
          />
        ))}

      {/* Clinical Narrative / Notes & Signature */}
      {shouldShowNotesAndSign && (
        <>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#403C6F",
              fontWeight: 500,
              mb: 1,
              display: "block",
              letterSpacing: "0.5px",
            }}
          >
            Clinical Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={clinicalNotes}
            onChange={(e) => setClinicalNotes(e.target.value)}
            placeholder="Enter clinical notes..."
            disabled={readOnly}
            sx={{
              mb: 4,
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: fieldBackground,
                cursor: inputCursor,
                "& fieldset": {
                  borderColor: borderColor,
                  borderWidth: "1px !important",
                },
                "&:hover fieldset": {
                  borderColor: hoverBorderColor,
                  borderWidth: "1px !important",
                },
                "&.Mui-focused fieldset": {
                  borderColor: `${primaryColor} !important`,
                  borderWidth: "1px !important",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#F3F4F6",
                  cursor: "not-allowed",
                  "& fieldset": {
                    borderColor: `${borderColor} !important`,
                    borderWidth: "1px !important",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  cursor: inputCursor,
                },
                "& .MuiOutlinedInput-input.Mui-disabled": {
                  color: "#10100F !important",
                  WebkitTextFillColor: "#10100F !important",
                },
              },
            }}
          />

          {/* Electronic Signature Section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              backgroundColor: "#FAFAFD",
              border: `1.5px solid ${borderColor}`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <DrawIcon sx={{ color: primaryColor, fontSize: 20 }} />
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ color: primaryColor }}
              >
                Signature
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 6, mb: 3, flexWrap: "wrap" }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: secondaryColor,
                    display: "block",
                    mb: 0.5,
                    letterSpacing: "0.5px",
                  }}
                >
                  SIGNEE NAME
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ color: "#10100F" }}
                >
                  {displaySigneeName}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: secondaryColor,
                    display: "block",
                    mb: 0.5,
                    letterSpacing: "0.5px",
                  }}
                >
                  DATE / TIME
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ color: "#10100F" }}
                >
                  {currentTime}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: borderColor, mb: 3 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={readOnly ? true : Boolean(certification)}
                  onChange={(e) => setCertification(e.target.checked)}
                  disabled={readOnly}
                  sx={{
                    color:
                      certificationErrorColor ?? `${borderColor} !important`,
                    "& .MuiSvgIcon-root": {
                      color: certificationErrorColor,
                      fill: certificationErrorColor,
                    },
                    "&.Mui-checked": {
                      color: `${primaryColor} !important`,
                    },
                    "&.Mui-checked .MuiSvgIcon-root": {
                      color: `${primaryColor} !important`,
                      fill: `${primaryColor} !important`,
                    },
                    "&:hover": {
                      backgroundColor: certificationErrorColor
                        ? "rgba(211, 47, 47, 0.08) !important"
                        : undefined,
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: "#403C6F" }}>
                  I certify that the documentation provided is accurate and
                  reflects the clinical assessment performed.{" "}
                  <span style={{ color: "#D32F2F" }}>*</span>
                </Typography>
              }
            />
          </Paper>
        </>
      )}
    </Box>
  );
};

OutcomeAndSign.propTypes = {
  memberData: PropTypes.object,
  assigneeRnName: PropTypes.string,
  signeeName: PropTypes.string,
  secondaryColor: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  primaryColor: PropTypes.string.isRequired,
  selectedOutcome: PropTypes.object,
  setSelectedOutcome: PropTypes.func,
  clinicalNotes: PropTypes.string,
  setClinicalNotes: PropTypes.func,
  certification: PropTypes.bool,
  setCertification: PropTypes.func,
  outcomes: PropTypes.array,
  readOnly: PropTypes.bool,
  noteStartTime: PropTypes.string,
  noteCompletionTime: PropTypes.string,
  declinedReason: PropTypes.object,
  setDeclinedReason: PropTypes.func,
  selectedSubReason: PropTypes.object,
  setSelectedSubReason: PropTypes.func,
  declinedNotes: PropTypes.object,
  setDeclinedNotes: PropTypes.func,
  programMap: PropTypes.object,
  activePrograms: PropTypes.array,
  showOutcomesOnly: PropTypes.bool,
  showSignOnly: PropTypes.bool,
  showPreAssessmentHeader: PropTypes.bool,
  showNotesAndSign: PropTypes.bool,
  availableProgramGroups: PropTypes.array,
  isAddProgramDisabled: PropTypes.bool,
  showAssessmentTypeSelect: PropTypes.bool,
  isAssessmentTypeDisabled: PropTypes.bool,
  assessmentType: PropTypes.string,
  onAssessmentTypeChange: PropTypes.func,
  onProgramAssessmentTypeChange: PropTypes.func,
  onAddProgram: PropTypes.func,
  onRemoveProgram: PropTypes.func,
};

export default OutcomeAndSign;
